// @flow
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia, CircularProgress,
  Container,
  CssBaseline, Dialog, DialogContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Build as DraftIcon,
  HourglassEmpty as ValidatingIcon,
  WhatsApp as SupportIcon,
  Work as ProcessingIcon
} from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import routes from '../routes';
import { AuthContext, setAuthToken } from '../context/auth';
import SignProcess from '../model/SignProcess';
import { toSentence } from '../util/string';
import { whatsappURL } from '../util/vendor';
import ApiResponse from '../model/ApiResponse';
import useWindowDimensions from '../component/useWindowDimensions';

const baseUrl = process.env.PUBLIC_URL || '';
const imageExt = 'png';
const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(2),
  },
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  validating: {
    color: theme.palette.grey[ '700' ],
  },
  processing: {
    color: theme.palette.grey[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error[ '600' ],
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(14),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  bottomButton: {
    marginTop: theme.spacing(2),
  },
  verificationImage: {
    display: 'block',
    margin: '0 auto',
    width: '99.5%',
    height: '99.5%',
  },
  noVisible: {
    visibility: 'hidden',
    height: 0,
  },
  visible: {
    visibility: 'visible',
  },
}));

type Props = {
  isRetailClient: boolean,
  match?: any,
};

const VerificationTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    maxWidth: 400,
    maxHeight: 400,
    width: '40%',
    height: '40%',
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const Selfie = (props: Props) => {
  const { width } = useWindowDimensions();
  const { user, doGet } = useContext(AuthContext);
  const { t } = useTranslation();
  const { match, isRetailClient } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [supportPhone, setSupportPhone] = useState<?string>();
  const [agentPhone, setAgentPhone] = useState<?string>();
  const [process, setProcess] = useState<?SignProcess>();
  const [photoBlob, setPhotoBlob] = useState<?Blob>();
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<?string>();

  let videoRef = useRef(null);
  let photoRef = useRef(null);

  const loadData = () => {
    if (!process
      && match && match.params
      && match.params.code && match.params.token) {
      let path = 'retail';
      setAuthToken(null);
      if (!isRetailClient) {
        setAuthToken(match.params.token);
        path = 'token';
      }
      doGet(`/customer/${path}-sign-process/${match.params.code}/${match.params.token}`)
        .then((response: ApiResponse) => {
          if (response.ok) {
            setProcess(SignProcess.parse(response.data));
          } else {
            setError(response.message || 'Error');
          }
        });
      if (!supportPhone) {
        doGet('/customer/support-phone')
          .then((response: ApiResponse) => setSupportPhone(response.data));
      }
      if (!agentPhone) {
        doGet('/customer/agent-phone')
          .then((response: ApiResponse) => setAgentPhone(response.data));
      }
    }
  };

  useEffect(loadData, []);

  const getVideo = () => {
    navigator.mediaDevices
      .getUserMedia({
        video: true
      })
      .then((stream) => {
        let video = videoRef.current;
        video.srcObject = stream;
        video.play();
      })
      .catch((err) => {
        console.error(err);
        setError('Could not detect camera');
      });
  };

  useEffect(getVideo, [videoRef]);

  const sendPhoto = (): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise<boolean>(resolve => {
        process.saveVideo(new File([photoBlob], `${user.code}_selfie.${imageExt}`), true)
          .then((response: ApiResponse) => {
            if (response.ok) {
              setProcess(SignProcess.parse(response.data));
              enqueueSnackbar(t('Verification saved'), {
                variant: 'success',
              });
              enqueueSnackbar(t('Your process is VIDEO_LOADED'), {
                variant: 'success',
              });
            } else {
              enqueueSnackbar(t(response.message), {
                variant: 'error',
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise<boolean>(resolve => resolve(false));
  };

  if (!match || !match.params || !match.params.code) {
    return <Redirect to={routes.home}/>;
  }

  const imageWidth = () => {
    return width / 3 >= 300 ? width / 3 : 300;
  };

  const takePicture = () => {
    const width = imageWidth();
    let video = videoRef.current;
    let photo = photoRef.current;
    photo.width = width;
    photo.height = width;
    let ctx = photo.getContext('2d');
    ctx.drawImage(video, 0, 0, width, width);
    photo.toBlob((blob) => {
      setPhotoBlob(blob);
    }, `image/${imageExt}`, 1.0);
  };

  const clearPhoto = () => {
    let photo = photoRef.current;
    let ctx = photo.getContext('2d');
    ctx.clearRect(0, 0, photo.width, photo.height);
    setPhotoBlob(null);
  };

  return (
    <Container component="main" maxWidth="xl">
      <CssBaseline/>
      <div className={classes.paper}>
        {(!process && !error) && [1, 2, 3, 4, 5].map((index) => (
          <Skeleton key={index}/>
        ))}
        {!!error && (
          <>
            <Typography variant="subtitle2" className={classes.error}>
              {t(error)}
            </Typography>
          </>
        )}
        {!!process && (
          <>
            <Typography variant="h5">
              {process.isDraft()
              && <DraftIcon className={classes.draft}/>}
              {(process.hasVideoLoaded() && ! process.isProcessing() && !process.isCompleted())
              && <ValidatingIcon className={classes.draft}/>}
              {(process.isProcessing() || process.isCompleted())
              && <ProcessingIcon className={classes.processing}/>}
              &nbsp;{t(toSentence(process.processType))} <small>({t(`Your process is ${process.state}`)})</small>
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Duration')}: {process.durationValue()} {t(process.durationLabel()).toLowerCase()}.
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Price')}: ${process.price.toFixed(2)}.
            </Typography>
            <small>&nbsp;</small>
            <Grid
              item
              xs={12} sm={12} md={12} lg={12}
              container
              alignItems="center"
              className={!!photoBlob ? classes.noVisible : classes.visible}
            >
              <Card variant="outlined" sx={{ maxWidth: 400 }}>
                <CardHeader title=""/>
                <CardMedia
                  component="video"
                  height={!photoBlob ? imageWidth() : imageWidth() / 3}
                  ref={videoRef}
                  autoPlay
                />
                <CardContent>
                  <Typography variant="subtitle1" color="text.secondary" component="div">
                    {t('Image intro')}
                  </Typography>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <VerificationTooltip
                      placement="top"
                      title={
                        <React.Fragment>
                          <img src={`${baseUrl}/verification-sample.jpg`}
                               alt=""
                               className={classes.verificationImage}/>
                        </React.Fragment>
                      }
                    >
                      <Button>{t('Check example')}</Button>
                    </VerificationTooltip>
                  </Grid>
                </CardContent>
                {!photoBlob && <CardActions>
                  <Button disabled={processing} variant="outlined" onClick={takePicture}>
                    <b>{t('Take Picture')}</b>
                  </Button>
                </CardActions>}
              </Card>
            </Grid>
            <Grid
              item
              xs={12} sm={12} md={12} lg={12}
              container
              alignItems="center"
              className={!photoBlob ? classes.noVisible : classes.visible}
            >
              <Card variant="outlined" sx={{ maxWidth: 400 }}>
                <CardHeader title=""/>
                <CardContent>
                  <canvas width={imageWidth()} ref={photoRef}/>
                </CardContent>
                {!!photoBlob && <CardActions>
                  <Button disabled={processing} variant="outlined" onClick={clearPhoto}>
                    <b>{t('Clear Image')}</b>
                  </Button>
                  <Button disabled={processing} variant="outlined" color="secondary" onClick={sendPhoto}>
                    <b>{t('Send Image')}</b>
                  </Button>
                </CardActions>}
              </Card>
            </Grid>
          </>
        )}
      </div>
      {(agentPhone && user) && <List>
        <ListItem
          button
          key="agentPhone"
          classes={{ button: classes.support }}
          onClick={() => {
            const url = whatsappURL(agentPhone, t('Agent support text', user));
            window.open(url, '_blank');
          }}
        >
          <ListItemIcon>
            <SupportIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Agent phone')}/>
        </ListItem>
      </List>}
      {(supportPhone && user) && <List>
        <ListItem
          button
          key="supportPhone"
          classes={{ button: classes.support }}
          onClick={() => {
            const url = whatsappURL(supportPhone, t('Support text', user));
            window.open(url, '_blank');
          }}
        >
          <ListItemIcon>
            <SupportIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Support phone')}/>
        </ListItem>
      </List>}
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        open={processing}
      >
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Selfie;
